import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppSelector, useToast } from '../../hooks';
import { ModeCalendar } from '../../pages/ListManyCalendars';
import { DeleteTypes, RecurrenceTypes } from '../../services/api.types';
import { dateLabelBlack } from '../../theme';
import {
  buildDeleteClass,
  buildRecurrenceDefaultValues,
  getRecurrenceTypeFromEvent,
  isSameRecurrence,
} from '../../utils/classes';
import { Button } from '../Button';
import { CalendarEvents, ClassOrTeacherBusyType } from '../Calendar';
import { Datepicker } from '../Datepicker';
import { DraggableModal } from '../DraggableModal';
import { Timepicker } from '../Timepicker';
import { eventRules } from './EventRules';
import { ModalRecurrence } from './ModalRecurrence';
import {
  UpdateClassDateModal,
  UpdateClassDateModalProps,
} from './UpdateClassDateModal';
import {
  CreateTeacherBusyReq,
  TeacherBusyModel,
} from '../../services/teacher-busy.types';
import { teacherBusyService } from '../../services/teacher-busy.service';
import { EventCalendarValues } from '.';
import { buildCreateTeacherBusy } from '../../utils/teacher-busy';
import { InputAutocomplete } from '../Inputs/Autocomplete';

export type TeacherBusyFormValues = Pick<
  EventCalendarValues,
  | 'startDate'
  | 'startHour'
  | 'endHour'
  | 'weekdays'
  | 'recurrenceType'
  | 'count'
  | 'until'
  | 'updateClassParams'
  | 'teacher'
  | 'intervalType'
  | 'intervalNumber'
>;

interface TeacherBusyEventFormProps {
  open: boolean;
  onClose: () => void;
  eventInfo: {
    mode: ModeCalendar;
    event: CalendarEvents | null;
  };
  onRemove?: (event: ClassOrTeacherBusyType) => void | Promise<void>;
  onCreate?: (event: ClassOrTeacherBusyType) => void | Promise<void>;
  onUpdate?: (
    oldEvent: ClassOrTeacherBusyType,
    newEvent: ClassOrTeacherBusyType
  ) => void | Promise<void>;
}

export const TeacherBusyEventForm = ({
  open,
  onClose,
  eventInfo,
  onRemove,
  onCreate,
  onUpdate,
}: TeacherBusyEventFormProps) => {
  const event = eventInfo.event;
  const mode = eventInfo.mode;

  const user = useAppSelector((state) => state.user);
  const toast = useToast();

  const recurrenceType = useMemo(
    () => getRecurrenceTypeFromEvent(event),
    [event]
  );

  const eventDefaultValues: TeacherBusyFormValues = useMemo(() => {
    const recurrenceValues = buildRecurrenceDefaultValues(
      recurrenceType,
      event
    );

    return {
      ...recurrenceValues,
      teacher: {
        id: event?.teacher?.id ?? user?.id ?? 0,
        displayName: event?.teacher?.displayName ?? user?.displayName ?? '',
      },
    };
  }, [event, recurrenceType, user]);

  const eventForm = useForm<TeacherBusyFormValues>({
    mode: 'all',
    defaultValues: eventDefaultValues,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [openUpdateClass, setOpenUpdateClass] = useState(false);
  const [handleUpdateClass, setHandleUpdateClass] = useState<{
    mode: UpdateClassDateModalProps['mode'];
    availableOptions: UpdateClassDateModalProps['availableOptions'];
    func: (type: DeleteTypes) => Promise<void>;
  }>({
    mode: 'delete',
    availableOptions: [],
    func: async () => undefined,
  });

  const handleEventError = (error: AxiosError) => {
    const data = error.response?.data as { message: string };
    let message = data.message || error.message;
    if (message.toLowerCase().includes('conflito'))
      message = 'Conflito de horário com outro evento';
    toast.error(message);
  };

  const handleCreate = async (data: CreateTeacherBusyReq) => {
    setIsLoading(true);
    await teacherBusyService
      .create(data)
      .then(async (res) => {
        await onCreate?.(res);
        toast.success('Evento Criado!');
        onClose();
      })
      .catch(handleEventError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChangeEvent = async (res: TeacherBusyModel) => {
    event && (await onUpdate?.(event, res));
  };

  const handleEdit = async (type: DeleteTypes) => {
    const values = eventForm.getValues();
    const recurrence =
      type === DeleteTypes.DATE ? RecurrenceTypes.ONCE : values.recurrenceType;

    const createClassParams = await buildCreateTeacherBusy({
      ...values,
      recurrenceType: recurrence,
    }).catch((error) => {
      toast.error(error);
    });
    const updateClassParams = buildDeleteClass(type, event);

    if (!createClassParams) return;

    setIsLoading(true);
    await teacherBusyService
      .update(Number(event?.id), {
        ...createClassParams,
        updateTeacherBusyType: updateClassParams.removeDateRecurrence,
        updateTeacherBusyDate: updateClassParams.date,
      })
      .then(async (res) => {
        await handleChangeEvent(res);
        toast.success('Evento alterado!');
        setOpenUpdateClass(false);
        onClose();
      })
      .catch(handleEventError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDelete = async (type: DeleteTypes) => {
    const deleteClassReq = buildDeleteClass(type, event);

    setIsLoading(true);
    const updatedEvent = await teacherBusyService
      .delete(Number(event?.id), deleteClassReq)
      .then((res) => {
        toast.success('Evento Excluído!');
        return res;
      })
      .finally(() => {
        setIsLoading(false);
      });

    await onRemove?.(updatedEvent);
    setOpenUpdateClass(false);
    onClose();
  };

  const onSubmit = async (data: TeacherBusyFormValues) => {
    const createClassParams = await buildCreateTeacherBusy(data).catch(
      (error) => {
        toast.error(error.message);
      }
    );
    if (!createClassParams) return;

    if (mode === ModeCalendar.create)
      return await handleCreate(createClassParams);

    if (mode === ModeCalendar.edit) {
      const availableOptions = [];
      const isSameClassRecurrence = isSameRecurrence(
        event,
        eventForm.getValues(),
        true
      );
      if (!isSameClassRecurrence) {
        availableOptions.push(DeleteTypes.UNTIL);
      } else {
        availableOptions.push(DeleteTypes.DATE, DeleteTypes.UNTIL);
      }

      setOpenUpdateClass(true);
      setHandleUpdateClass({
        mode: 'update',
        availableOptions,
        func: handleEdit,
      });
    }
  };

  const modalTitle = useMemo(() => {
    if (mode === ModeCalendar.create) return 'Criar horário ocupado';
    if (mode === ModeCalendar.edit) return 'Editar horário ocupado';
    return 'Visualizar horário ocupado';
  }, [mode]);

  return (
    <DraggableModal
      title={modalTitle}
      open={open}
      mode={mode}
      onClose={!isLoading ? onClose : () => undefined}
      onDelete={() => {
        setOpenUpdateClass(true);
        setHandleUpdateClass({
          mode: 'delete',
          availableOptions: [DeleteTypes.DATE, DeleteTypes.UNTIL],
          func: handleDelete,
        });
      }}
    >
      <FormProvider {...eventForm}>
        <form
          onSubmit={eventForm.handleSubmit(onSubmit)}
          className="w-full flex flex-col items-center justify-between p-6 gap-4"
        >
          <div className="w-full gap-2 flex justify-center items-start">
            <ThemeProvider theme={dateLabelBlack}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <Datepicker
                  disabled={mode === ModeCalendar.view}
                  labelOut={false}
                  name="startDate"
                  label="Data início"
                  rules={eventRules.dateRequired}
                />
                <Timepicker
                  labelOut={false}
                  disabled={mode === ModeCalendar.view}
                  name="startHour"
                  label="Horário início"
                  rules={eventRules.timeRequired}
                />
                <Timepicker
                  labelOut={false}
                  disabled={mode === ModeCalendar.view}
                  name="endHour"
                  label="Horário fim"
                  rules={eventRules.timeRequired}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </div>

          <ModalRecurrence mode={mode} disabled={mode === ModeCalendar.view} />

          <InputAutocomplete
            name="teacher"
            label="Nome do professor:"
            values={[]}
            disabled
          />

          {mode !== ModeCalendar.view && (
            <Button isLoading={isLoading} type="submit" className="w-1/2 mt-6">
              Salvar
            </Button>
          )}
        </form>
      </FormProvider>

      <UpdateClassDateModal
        mode={handleUpdateClass.mode}
        availableOptions={handleUpdateClass.availableOptions}
        open={openUpdateClass}
        onChoose={handleUpdateClass.func}
        onClose={() => setOpenUpdateClass(false)}
        isLoading={isLoading}
      />
    </DraggableModal>
  );
};
